import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import TabsView from 'components/TabsView'
import { currencyCodeList } from 'constants/currencyCodeList'
import { getGameUrl } from 'utils/helpers'
import useTopPerformance from './hooks/useTopPerformance'
import './styles/index.scss'

const SKELETON_ROW_COUNT = 5

/**
 * TopPerformanceV2 component displays a table of casino game winners with filtering options.
 * It shows three categories of winners: all winners, high winners, and lucky winners.
 * The component includes game thumbnails, usernames, wagers, multipliers, and payouts.
 *
 * @component
 * @returns {JSX.Element} A component displaying filtered lists of casino game winners
 */
const TopPerformanceV2 = () => {
  const navigate = useNavigate()
  const languageData = useSelector(state => state?.language?.languageData)
  const { selectedLanguageCode } = useSelector((state) => state.language)

  const [activeFilter, setActiveFilter] = useState('all') // 'all' | 'high' | 'lucky'

  const {
    allWinners = [],
    highWinners = [],
    luckyWinners = [],
    isLoading
  } = useTopPerformance(activeFilter)

  const chosenDataset = useMemo(() => {
    if (activeFilter === 'all') return allWinners || []
    if (activeFilter === 'high') return highWinners || []
    return luckyWinners || []
  }, [activeFilter, allWinners, highWinners, luckyWinners])

  const filteredRows = useMemo(() => {
    return (chosenDataset || []).map((winner, index) => {
      const payoutAmt = Number(winner?.totalWin)
      const currency = currencyCodeList?.EUR
      const gameId = winner?.gameId ?? winner?.id
      const gameName = winner?.gameName ?? winner?.game

      return {
        id: `${winner?.username || index}-${index}`,
        game: winner?.game || '-',
        gameId,
        gameName,
        provider: winner?.providerDisplayName ?? winner?.providerName,
        thumbnailUrl: winner?.thumbnailUrl,
        username: winner?.username || '-',
        wager: winner?.totalBet,
        wagerDisplay: `${currency}${winner?.totalBet}`,
        multiplier: winner?.multiplier || '-',
        payout: payoutAmt,
        payoutDisplay: `${currency}${payoutAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        currencySymbol: currencyCodeList?.EUR
      }
    })
  }, [chosenDataset])

  const t = {
    all: languageData?.allWins || 'All',
    highWins: languageData?.highWins || 'High Wins',
    luckyWins: languageData?.luckyWins || 'Lucky Wins',
    game: languageData?.topWinsGame || 'Game',
    username: languageData?.topWinsUsername || 'Username',
    time: languageData?.topWinsTime || 'Time',
    wager: languageData?.topWinsWager || 'Wager',
    multiplier: languageData?.topWinsMultiplier || 'Multiplier',
    payout: languageData?.topWinsPayout || 'Payout'
  }

  const getInitials = str => (str || '').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()

  const tableHead = (
    <div className='top-performance-v2__table-head' role='rowgroup'>
      <div className='top-performance-v2__row top-performance-v2__row--head' role='row'>
        <div className='top-performance-v2__cell top-performance-v2__cell--game' role='columnheader'>{t.game}</div>
        <div className='top-performance-v2__cell top-performance-v2__cell--username' role='columnheader'>{t.username}</div>
        {/* <div className='top-performance-v2__cell top-performance-v2__cell--time' role='columnheader'>{t.time}</div> */}
        <div className='top-performance-v2__cell top-performance-v2__cell--wager' role='columnheader'>{t.wager}</div>
        <div className='top-performance-v2__cell top-performance-v2__cell--multiplier' role='columnheader'>{t.multiplier}</div>
        <div className='top-performance-v2__cell top-performance-v2__cell--payout' role='columnheader'>{t.payout}</div>
      </div>
    </div>
  )

  const handleGameClick = (row) => {
    if (!row?.gameId || !row?.gameName) return
    navigate(getGameUrl(
      (selectedLanguageCode || 'en').toLowerCase(),
      row.gameId,
      row.gameName,
      row.provider
    ))
  }

  return (
    <div className='top-performance-v2'>
      <div className='wrapper-block'>
        {/* Header tabs using TabsView */}
        <TabsView
          className='top-performance-v2__filters'
          items={[
            { key: 'all', label: t.all },
            { key: 'high', label: t.highWins },
            { key: 'lucky', label: t.luckyWins }
          ]}
          activeKey={activeFilter}
          onSelect={(key) => setActiveFilter(key)}
          variant='pills'
          size='sm'
        />

        {/* Skeleton, Table or Empty State */}
        {isLoading && (
          <div className='top-performance-v2__table' role='table' aria-busy='true' aria-label='Top performance table loading'>
            {tableHead}
            <div className='top-performance-v2__table-body' role='rowgroup'>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                <div key={`skeleton-${index}`} className='top-performance-v2__row' role='row'>
                  <div className='top-performance-v2__cell top-performance-v2__cell--game' role='cell'>
                    <div className='top-performance-v2__game'>
                      <div className='top-performance-v2__game-thumb top-performance-v2__skeleton' aria-hidden />
                      <span className='top-performance-v2__skeleton top-performance-v2__skeleton--text' />
                    </div>
                  </div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--username' role='cell'>
                    <span className='top-performance-v2__skeleton top-performance-v2__skeleton--text' />
                  </div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--wager' role='cell'>
                    <span className='top-performance-v2__skeleton top-performance-v2__skeleton--text' />
                  </div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--multiplier' role='cell'>
                    <span className='top-performance-v2__skeleton top-performance-v2__skeleton--text' />
                  </div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--payout' role='cell'>
                    <span className='top-performance-v2__skeleton top-performance-v2__skeleton--text' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && filteredRows.length === 0 && (
          <div className='top-performance-v2__empty' role='status'>
            {languageData?.noDataFound || 'No Data Available'}
          </div>
        )}

        {!isLoading && filteredRows.length > 0 && (
          <div className='top-performance-v2__table' role='table' aria-label='Top performance table'>
            {tableHead}
            <div className='top-performance-v2__table-body' role='rowgroup'>
              {filteredRows.map((r) => (
                <div key={r.id} className={`top-performance-v2__row ${r.isDisabled ? 'is-disabled' : ''}`} role='row'>
                  <div className='top-performance-v2__cell top-performance-v2__cell--game' role='cell'>
                    <div
                      className='top-performance-v2__game'
                      onClick={() => handleGameClick(r)}
                    >
                      <div className='top-performance-v2__game-thumb' aria-hidden>
                        {r.thumbnailUrl
                          ? (
                            <img src={r.thumbnailUrl} alt={r.game} />
                            )
                          : (
                              getInitials(r.game)
                            )}
                      </div>
                      <span className='top-performance-v2__game-name'>{r.game}</span>
                    </div>
                  </div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--username' role='cell'>{r.username}</div>
                  {/* <div className='top-performance-v2__cell top-performance-v2__cell--time' role='cell'>{r.time}</div> */}
                  <div className='top-performance-v2__cell top-performance-v2__cell--wager' role='cell'>{r.wagerDisplay}</div>
                  <div className='top-performance-v2__cell top-performance-v2__cell--multiplier' role='cell'>{r?.multiplier ? Number(r.multiplier).toFixed(2) : '-'}</div>
                  <div className={`top-performance-v2__cell top-performance-v2__cell--payout ${r.payout > 0 ? 'is-positive' : 'is-zero'}`} role='cell'>
                    {r.payout > 0 ? r.payoutDisplay : `${r.currencySymbol}0.00`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TopPerformanceV2
